'use client'

import { getPipelines, PIPELINE_TYPE_LABELS, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useState } from 'react'
import { MdTrendingUp } from 'react-icons/md'

interface PipelineSelectorProps {
    value?: number
    onChange: (pipelineId: number | undefined) => void
    required?: boolean
    className?: string
}

const STORAGE_KEY_PREFIX = 'lastPipelineId_'

export default function PipelineSelector({ value, onChange, required, className }: PipelineSelectorProps) {
    const { user } = useAuth()
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [loading, setLoading] = useState(true)

    // O funil é da ORGANIZAÇÃO: a última escolha também (antes era por filial).
    const storageKey = `${STORAGE_KEY_PREFIX}org_${user?.organizationId || ''}`

    const loadPipelines = useCallback(async () => {
        if (!user?.organizationId) return
        setLoading(true)
        try {
            const response = await getPipelines()
            if (response.success) {
                const active = response.result.filter((p: SalesPipeline) => p.active)
                setPipelines(active)

                // Auto-selecionar só funil DA FILIAL atual. O `isDefault` agora é o padrão
                // da ORGANIZAÇÃO (a triagem, sem filial): pré-selecioná-lo mandava todo
                // atendimento para a triagem. Sem funil da filial, fica vazio e o backend
                // escolhe o de entrada da filial do atendimento.
                if (!value && active.length > 0) {
                    const unitPipelines = active.filter((p: SalesPipeline) => p.companyId === user?.companyId)
                    const savedId = localStorage.getItem(storageKey)
                    const savedPipeline = savedId
                        ? unitPipelines.find((p: SalesPipeline) => p.id === parseInt(savedId))
                        : null
                    const unitDefault = unitPipelines.find((p: SalesPipeline) => p.isUnitDefault)
                    const selected = savedPipeline || unitDefault || unitPipelines[0]
                    if (selected) {
                        onChange(selected.id)
                    }
                }
            }
        } finally {
            setLoading(false)
        }
    }, [user?.organizationId])

    useEffect(() => {
        loadPipelines()
    }, [loadPipelines])

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value ? parseInt(e.target.value) : undefined
        onChange(id)
        if (id) {
            localStorage.setItem(storageKey, String(id))
        }
    }

    if (loading) {
        return (
            <div className={`form-control ${className || ''}`}>
                <label className="label">
                    <span className="label-text font-medium flex items-center gap-1.5">
                        <MdTrendingUp className="text-secondary" />
                        Funil de Vendas
                    </span>
                </label>
                <select className="select select-bordered w-full" disabled>
                    <option>Carregando...</option>
                </select>
            </div>
        )
    }

    if (pipelines.length === 0) return null

    return (
        <div className={`form-control ${className || ''}`}>
            <label className="label">
                <span className="label-text font-medium flex items-center gap-1.5">
                    <MdTrendingUp className="text-secondary" />
                    Funil de Vendas
                    {required && <span className="text-error">*</span>}
                </span>
            </label>
            <select
                className="select select-bordered w-full"
                value={value || ''}
                onChange={handleChange}
                required={required}
            >
                {!required && <option value="">Selecione um funil</option>}
                {pipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                        {pipeline.company?.name ? ` — ${pipeline.company.name}` : pipeline.isDefault ? ' — Triagem' : ''}
                        {pipeline.isDefault ? ' (Padrão)' : ''}
                        {' - '}
                        {PIPELINE_TYPE_LABELS[pipeline.type] || pipeline.type}
                    </option>
                ))}
            </select>
        </div>
    )
}
